<template>
	<div class="page" v-if="page">
		<section class="section">
			<div class="container is-fullhd">
				<div class="columns is-multiline">
					<div class="column is-full">
						<div class="subtitle has-text-centered">Players Drafted by NFL Team</div>

						<Chart :config="charts.players_by_nfl_team" />
					</div>
					<div class="column is-half">
						<div class="subtitle has-text-centered">Players Drafted by Position</div>

						<Chart :config="charts.players_by_position" />
					</div>
					<div class="column is-half">
						<div class="box content">
							<h2>Draft Performance</h2>

							<table class="table is-fullwidth is-hidden-mobile">
								<thead>
									<tr>
										<th class="has-text-right">#</th>
										<th>Team</th>
										<th class="has-text-right">Performance</th>
										<th class="has-text-right">Owned</th>
										<th class="has-text-right">Perf. Owned</th>
									</tr>
								</thead>
								<tbody>
									<tr v-for="(team, index) in page.teams">
										<td class="has-text-right">{{ index + 1 }}</td>
										<td><TeamLink :teamKey="team.key" :teamName="team.name" /></td>
										<td class="has-text-right">{{ team.draft_performance }}</td>
										<td class="has-text-right">{{ team.draft_roster_owned }}</td>
										<td class="has-text-right">{{ team.draft_performance_owned }}</td>
									</tr>
								</tbody>
							</table>

							<table class="table is-fullwidth is-hidden-tablet">
								<thead>
									<tr>
										<th class="has-text-right">#</th>
										<th>Team</th>
										<th class="has-text-right">Perf.</th>
										<th class="has-text-right">Owned</th>
									</tr>
								</thead>
								<tbody>
									<tr v-for="(team, index) in page.teams">
										<td class="has-text-right">{{ index + 1 }}</td>
										<td><TeamLink :teamKey="team.key" :teamName="team.name" /></td>
										<td class="has-text-right">{{ team.draft_performance }}</td>
										<td class="has-text-right">{{ team.draft_roster_owned }}</td>
									</tr>
								</tbody>
							</table>
						</div>
					</div>
				</div>
			</div>
		</section>

		<section class="section">
			<div class="container is-fullhd">
				<div class="box content" v-if="!selectedTeam">
					<div class="is-flex is-flex-apart">
						<h2>Draft Results</h2>

						<Dropdown
							optionCaption="Select a team..."
							:options="page.draft_order"
							:optionText="(option) => option.name"
							:optionValue="(option) => option.key"
							:handleSelectedOption="(index, team) => setSelectedTeam(team.key)"
						/>
					</div>

					<table class="table is-fullwidth is-hoverable is-narrow is-hidden-mobile">
						<thead>
							<tr>
								<th>Pick</th>
								<th>Team</th>
								<th>Player</th>
								<th>Team/Pos.</th>
								<th>Draft Pos.</th>
								<th>Actual Pos.</th>
								<th class="has-text-right">Pos. Diff</th>
							</tr>
						</thead>
						<tbody>
							<template v-for="draft_round in page.draft_rounds">
								<tr
									v-for="(draft_result, index) in draft_round.picks"
									:class="{ 'thick-line': draft_round.round > 1 && index == 0 }"
								>
									<td>{{ draft_result.pick }}</td>
									<td><TeamLink :teamKey="draft_result.team.key" :teamName="draft_result.team.name" /></td>
									<td>{{ draft_result.player.name }}</td>
									<td>{{ draft_result.player.team_abbr }} - {{ draft_result.player.position }}</td>
									<td>{{ draft_result.player.position }}{{ draft_result.draft_position_rank }}</td>
									<td>{{ draft_result.player.position }}{{ draft_result.actual_position_rank }}</td>
									<td class="has-text-right">{{ draft_result.position_rank_diff }}</td>
								</tr>
							</template>
						</tbody>
					</table>

					<table class="table is-fullwidth is-hoverable is-narrow is-hidden-tablet">
						<tbody>
							<template v-for="draft_round in page.draft_rounds">
								<tr v-for="draft_result in draft_round.picks">
									<td>{{ draft_result.pick }}</td>
									<td>
										<TeamLink :teamKey="draft_result.team.key" :teamName="draft_result.team.name" /><br />
										{{ draft_result.player.name }}
									</td>
									<td>
										{{ draft_result.player.team_abbr }}<br />
										{{ draft_result.player.position }}{{ draft_result.actual_position_rank }}
									</td>
								</tr>
							</template>
						</tbody>
					</table>
				</div>

				<div class="box content" v-else>
					<div class="is-flex is-flex-apart">
						<h2><TeamLink :teamKey="selectedTeam.key" :teamName="selectedTeam.name" />'s Draft</h2>

						<a href="#" class="button is-text" @click.prevent="setSelectedTeam(null)">Back to All</a>
					</div>

					<table class="table is-fullwidth is-hoverable is-narrow is-hidden-mobile">
						<thead>
							<tr>
								<th>Pick</th>
								<th>Player</th>
								<th>Team/Pos.</th>
								<th>Draft Pos.</th>
								<th>Actual Pos.</th>
								<th class="has-text-right">Pos. Diff</th>
							</tr>
						</thead>
						<tbody>
							<tr v-for="draft_result in selectedTeamDraftPicks">
								<td>{{ draft_result.pick }}</td>
								<td>{{ draft_result.player.name }}</td>
								<td>{{ draft_result.player.team_abbr }} - {{ draft_result.player.position }}</td>
								<td>{{ draft_result.player.position }}{{ draft_result.draft_position_rank }}</td>
								<td>{{ draft_result.player.position }}{{ draft_result.actual_position_rank }}</td>
								<td class="has-text-right">{{ draft_result.position_rank_diff }}</td>
							</tr>
						</tbody>
						<tfoot>
							<tr>
								<td colspan="6" class="has-text-right">
									Draft Performance: {{ selectedTeam.draft_performance }}<br />
									Draft Rank: {{ selectedTeam.rank_draft_performance }}
								</td>
							</tr>
						</tfoot>
					</table>

					<table class="table is-fullwidth is-hoverable is-narrow is-hidden-tablet">
						<tbody>
							<tr v-for="draft_result in selectedTeamDraftPicks">
								<td>{{ draft_result.pick }}</td>
								<td>{{ draft_result.player.name }}</td>
								<td>{{ draft_result.player.team_abbr }}</td>
								<td>{{ draft_result.player.position }}{{ draft_result.actual_position_rank }}</td>
							</tr>
						</tbody>
					</table>
				</div>
			</div>
		</section>
	</div>
</template>

<script>
import Chart from "@/components/Chart"
import Dropdown from "@/components/Dropdown"
import TeamLink from "@/components/TeamLink"

export default {
	components: {
		Chart,
		Dropdown,
		TeamLink,
	},
	data() {
		return {
			selectedTeamKey: null,
		}
	},
	computed: {
		league() {
			return this.$store.getters.activeLeague
		},
		page() {
			return this.$store.getters.activePage
		},
		selectedTeam() {
			return this.page.teams.find((team) => team.key == this.selectedTeamKey)
		},
		selectedTeamDraftPicks() {
			return !this.selectedTeamKey
				? []
				: this.page.draft_rounds
						.map((draft_round) => draft_round.picks.find((pick) => pick.team.key == this.selectedTeamKey))
						.filter(Boolean)
		},
		charts() {
			if (!this.page) return {}

			return {
				players_by_nfl_team: {
					type: "bar",
					series: [
						{
							name: "Players",
							data: this.page.graph_datas.players_by_nfl_team.map((item) => item.value),
						},
					],
					height: 500,
					options: {
						xaxis: {
							categories: this.page.graph_datas.players_by_nfl_team.map((item) => item.team_abbr),
						},
						tooltip: {
							x: {
								formatter: (label, data) => this.page.graph_datas.players_by_nfl_team[data.dataPointIndex].team_name,
							},
						},
						plotOptions: {
							bar: {
								horizontal: false,
							},
						},
					},
				},
				players_by_position: {
					type: "pie",
					series: this.page.graph_datas.players_by_position.map((item) => item.value),
					options: {
						labels: this.page.graph_datas.players_by_position.map((item) => item.position),
					},
				},
			}
		},
	},
	methods: {
		setSelectedTeam(key) {
			this.selectedTeamKey = key
		},
	},
	mounted() {
		this.$store.dispatch("setActivePage", "draft_dashboard")
	},
}
</script>

<style></style>
